"use client";

import { IconCross, IconMagnifier, IconSearchEmpty } from "@repo/assets/images";
import { PopularSearch, QuickSearch } from "@repo/components";
import { useDebounce } from "@repo/lib/hooks/common";
import { useMiniSearch } from "@repo/lib/hooks/search";
import { useTrackSearchQuery } from "@repo/lib/hooks/webengage";
import { OffCanvasDrawer } from "@repo/ui";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import NProgress from "nprogress";
import { useCallback, useEffect, useRef, useState } from "react";
const minChar = 1;
const SearchForm = ({
  handleSubmitSearch,
  handleSearchPopOver,
  inputRef,
  handleChange,
  renderSearchResults,
  handleClearSearchTerm
}) => {
  const t = useTranslations("Common");
  return <form onSubmit={handleSubmitSearch} className="w-full" data-sentry-component="SearchForm" data-sentry-source-file="Search.jsx">
      <div className="relative">
        <IconMagnifier className="z-70 text-miniSearch-icon absolute start-3 top-1/2 w-5 -translate-y-1/2 fill-current rtl:rotate-90" data-sentry-element="IconMagnifier" data-sentry-source-file="Search.jsx" />
        <input ref={inputRef} type="text" className="border-miniSearch-border text-miniSearch-heading placeholder:text-miniSearch-inputPlaceholder md:z-60 bg-miniSearch-bg h-11 w-full border-y px-10 text-base font-medium focus:outline-none" placeholder={t("Search_Product_Category")} onChange={handleChange} onClick={handleSearchPopOver} />
        {inputRef.current?.value && <button type="button" onClick={handleClearSearchTerm}>
            <IconCross className="z-70 text-miniSearch-icon absolute end-5 top-1/2 -translate-y-1/2 fill-current rtl:rotate-90" />
          </button>}
      </div>
      {renderSearchResults()}
    </form>;
};
const Search = () => {
  const router = useRouter();
  const [term, setTerm] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const inputRef = useRef(null);
  const debouncedSearchTerm = useDebounce(term, 300);
  const t = useTranslations("Common");
  const {
    trackSearchQueryEvent
  } = useTrackSearchQuery();
  const {
    handlePopularSearch,
    handleQuickSearch,
    popuparSearch,
    quickSearch,
    isPending
  } = useMiniSearch();
  const handleOpenSearchDrawer = useCallback(async () => {
    setOpenDrawer(prev => !prev);
    if (!openDrawer) {
      // Focus on search input when opening search
      inputRef.current?.focus();
      await handlePopularSearch();
    }
  }, [openDrawer, handlePopularSearch]);
  const handleSubmitSearch = useCallback(event => {
    event.preventDefault();
    if (term) {
      NProgress.start();
      trackSearchQueryEvent(term);
      setOpenDrawer(false);
      router.push(`/search?term=${term}`);
    }
  }, [term, router, trackSearchQueryEvent]);
  const handleChange = useCallback(e => {
    setTerm(e.target.value);
  }, [setTerm]);
  const handleClearSearchTerm = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      inputRef.current.focus();
      setTerm("");
    }
  }, [setTerm]);

  // call api and wait for the state to update after getting the data
  const fetchSearchResult = useCallback(async term => {
    if (term.length >= minChar) {
      await handleQuickSearch(term);
    }
  }, [handleQuickSearch]);
  const hasPopularSearchItems = useCallback(() => {
    if (!popuparSearch?.sections?.length) return false;
    return popuparSearch.sections.some(section => Array.isArray(section.items) && section.items.length > 0);
  }, [popuparSearch]);

  // Call fetch search result function to call api after debouncing
  useEffect(() => {
    if (debouncedSearchTerm) {
      fetchSearchResult(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, fetchSearchResult]);
  const renderSearchResults = () => {
    if (debouncedSearchTerm?.length < minChar) {
      if (!hasPopularSearchItems() && !isPending) {
        return <div className="py-10 text-center">
            <IconSearchEmpty className="text-miniSearch-searchNotFoundIconText fill-miniSearch-searchNotFoundIconBg mx-auto" />
            <p className="font-heading text-miniSearch-heading rtl:font-arabic py-2 text-xl font-medium">
              {t("No_Popular_Searches_Found")}
            </p>
          </div>;
      }
      return <PopularSearch popuparSearch={popuparSearch} isPending={isPending} onClick={handleOpenSearchDrawer} />;
    }
    return <QuickSearch quickSearch={quickSearch} isPending={isPending} debouncedSearchTerm={debouncedSearchTerm} onClick={handleOpenSearchDrawer} data-sentry-element="QuickSearch" data-sentry-component="renderSearchResults" data-sentry-source-file="Search.jsx" />;
  };
  return <>
      <button onClick={handleOpenSearchDrawer} className="touch-manipulation px-2 text-[16px]" aria-label={t("Search")}>
        <IconMagnifier className="text-header-icon w-5 fill-current md:w-7" data-sentry-element="IconMagnifier" data-sentry-source-file="Search.jsx" />
      </button>
      <OffCanvasDrawer open={openDrawer} handleClose={handleOpenSearchDrawer} title={t("Search")} position="right" data-sentry-element="OffCanvasDrawer" data-sentry-source-file="Search.jsx">
        <div className="py-2">
          <SearchForm handleSubmitSearch={handleSubmitSearch} inputRef={inputRef} handleChange={handleChange} renderSearchResults={renderSearchResults} handleClearSearchTerm={handleClearSearchTerm} data-sentry-element="SearchForm" data-sentry-source-file="Search.jsx" />
        </div>
      </OffCanvasDrawer>
    </>;
};
Search.displayName = "Search";
export default Search;